/**
 * SmartSelect — searchable picker with evidence-based options + "Other (specify)".
 * Iter 71.
 *
 * Closes the user's gap: every PE row (Special tests, ROM joint, MMT muscle,
 * Auscultation site, Postural finding, etc.) should offer a curated list of
 * evidence-based options PLUS an "Other (specify)" fallback that opens a
 * free-text input — so the clinician picks fast but is never locked out.
 *
 * Behaviour:
 *   • Closed: shows the current value as a chip / text.
 *   • Open: dropdown with search filter, options, and a sticky "Other" row.
 *   • Picking "Other (specify)" flips the field into free-text input mode.
 *   • Each option may carry { value, label, ref } — `ref` triggers a popup
 *     reference badge with a clickable link.
 *
 * Props:
 *   value         current string value
 *   onChange      (newValue: string) => void
 *   options       Array<{value, label?, ref?, refUrl?}>
 *   placeholder   string
 *   testid        data-testid prefix
 *   inputClass    extra className for the inner input
 *   compact       small layout (default false)
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Search, X, ChevronDown, Edit3, BookOpen } from 'lucide-react';
import EvidenceModal from './EvidenceModal';
import { TEST_ACCURACY } from '../lib/test-accuracy';

export default function SmartSelect({
  value = '', onChange, options = [], placeholder = 'Pick or specify…',
  testid = 'smart-select', inputClass = '', compact = false,
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [otherMode, setOtherMode] = useState(false);
  const [evidenceFor, setEvidenceFor] = useState(null);  // test name to show modal for
  const wrap = useRef(null);

  // Auto-detect "other" when the value isn't in the curated list.
  useEffect(() => {
    if (!value) { setOtherMode(false); return; }
    const inList = options.some(o => (o.value || o.label) === value);
    setOtherMode(!inList);
  }, [value, options]);

  useEffect(() => {
    const h = (e) => { if (wrap.current && !wrap.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const filtered = useMemo(() => {
    const ql = q.trim().toLowerCase();
    if (!ql) return options;
    return options.filter(o =>
      (o.label || o.value || '').toLowerCase().includes(ql) ||
      (o.ref || '').toLowerCase().includes(ql)
    );
  }, [options, q]);

  const pick = (val) => {
    if (val === '__other__') {
      setOtherMode(true);
      onChange?.('');  // clear so the input is empty + ready
      setOpen(false);
      return;
    }
    setOtherMode(false);
    onChange?.(val);
    setOpen(false);
    setQ('');
  };

  // When in "other" mode, render a free-text input with a small "back to list" link
  if (otherMode) {
    return (
      <div className="relative flex items-center gap-1" ref={wrap}>
        <input className={`input ${compact ? '!py-1 !text-xs' : ''} ${inputClass}`}
               value={value || ''}
               onChange={(e) => onChange?.(e.target.value)}
               placeholder="Specify…"
               data-testid={`${testid}-other-input`}/>
        <button type="button"
                onClick={() => { setOtherMode(false); onChange?.(''); }}
                className="text-[10px] text-fg-muted hover:text-brand whitespace-nowrap"
                title="Pick from list instead"
                data-testid={`${testid}-back`}>
          <Edit3 className="w-3 h-3"/>
        </button>
      </div>
    );
  }

  return (
    <div className="relative" ref={wrap}>
      <div className="flex items-center gap-1">
        <button type="button"
                onClick={() => setOpen(o => !o)}
                className={`input flex items-center justify-between gap-2 flex-1 text-left ${compact ? '!py-1 !text-xs' : ''} ${inputClass}`}
                data-testid={`${testid}-trigger`}>
          <span className={value ? '' : 'text-fg-muted'}>{value || placeholder}</span>
          <ChevronDown className="w-3 h-3 text-fg-muted flex-shrink-0"/>
        </button>
        {/* Iter 72 — when the current value has diagnostic-accuracy data,
            show a compact reference badge that opens the evidence modal.  */}
        {value && TEST_ACCURACY[value] && (
          <button type="button"
                  onClick={(e) => { e.stopPropagation(); setEvidenceFor(value); }}
                  title={`Show diagnostic accuracy for ${value}`}
                  className="px-1.5 py-1 rounded bg-[#003D80]/10 text-[#003D80] hover:bg-[#003D80] hover:text-white transition flex-shrink-0"
                  data-testid={`${testid}-evidence-btn`}>
            <BookOpen className="w-3 h-3"/>
          </button>
        )}
      </div>
      {open && (
        <div className="absolute z-30 mt-1 left-0 right-0 min-w-[260px] bg-white border border-soft rounded-lg shadow-xl max-h-72 overflow-auto">
          <div className="p-2 sticky top-0 bg-white border-b border-soft flex items-center gap-2">
            <Search className="w-3.5 h-3.5 text-fg-muted"/>
            <input autoFocus className="flex-1 text-xs outline-none"
                   placeholder="Type to filter…"
                   value={q} onChange={(e) => setQ(e.target.value)}
                   data-testid={`${testid}-search`}/>
            <span className="text-[10px] text-fg-muted">{filtered.length}</span>
          </div>
          {filtered.map(o => {
            const optVal = o.value || o.label;
            const hasEvidence = !!TEST_ACCURACY[optVal];
            return (
              <div key={optVal}
                   className={`relative px-3 py-2 text-xs flex items-center gap-2 hover:bg-[#F2EBDB] ${value === optVal ? 'bg-[#F2EBDB] font-semibold' : ''}`}>
                <button type="button"
                        onClick={() => pick(optVal)}
                        className="flex-1 text-left"
                        data-testid={`${testid}-opt-${optVal.toLowerCase().replace(/[^a-z0-9]+/g, '-').slice(0, 32)}`}>
                  {o.label || o.value}
                </button>
                {o.ref && (
                  hasEvidence ? (
                    <button type="button"
                            onClick={(e) => { e.stopPropagation(); setEvidenceFor(optVal); }}
                            title="Show diagnostic accuracy"
                            className="text-[9px] inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-[#003D80]/10 text-[#003D80] hover:bg-[#003D80] hover:text-white transition font-semibold flex-shrink-0"
                            data-testid={`${testid}-ref-${optVal.toLowerCase().replace(/[^a-z0-9]+/g, '-').slice(0, 32)}`}>
                      <BookOpen className="w-2.5 h-2.5"/> {o.ref}
                    </button>
                  ) : (
                    <span title={`Reference: ${o.ref}`} className="text-[9px] text-fg-muted inline-flex items-center gap-0.5 flex-shrink-0">
                      <BookOpen className="w-2.5 h-2.5"/> {o.ref}
                    </span>
                  )
                )}
              </div>
            );
          })}
          <button type="button"
                  onClick={() => pick('__other__')}
                  data-testid={`${testid}-other`}
                  className="w-full text-left px-3 py-2 text-xs border-t border-soft bg-bg-secondary/30 hover:bg-[#F2EBDB] sticky bottom-0 flex items-center gap-2 text-brand font-semibold">
            <Edit3 className="w-3 h-3"/> Other (specify…)
          </button>
        </div>
      )}
      {evidenceFor && <EvidenceModal testName={evidenceFor} onClose={() => setEvidenceFor(null)}/>}
    </div>
  );
}
